"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Helper for working with i18n.
 */
const IntlHelper = {
    /**
     * This calculates the best unit to use for formatting based off of offset from now.
     * Like moment.js or date-fns, just much smaller and less precise.
     *
     * @param intl - an intl instance
     * @param pastTimeInMs - the time in the past in milliseconds.
     * @param currentTimeInMs - the current time in milliseconds
     */
    niceRelativeTime(intl, pastTimeInMs, currentTimeInMs = Date.now()) {
        if (!pastTimeInMs) {
            return '';
        }
        const seconds = (currentTimeInMs - pastTimeInMs) / 1000;
        let value = seconds;
        let unit = 'second';
        const minInSeconds = 60;
        const hourInSeconds = minInSeconds * 60;
        const dayInSeconds = hourInSeconds * 24;
        const monthInSeconds = dayInSeconds * 31;
        const yearInSeconds = dayInSeconds * 365;
        if (seconds >= yearInSeconds) {
            value = seconds / yearInSeconds;
            unit = 'year';
        }
        else if (seconds >= monthInSeconds) {
            value = seconds / monthInSeconds;
            unit = 'month';
        }
        else if (seconds >= dayInSeconds) {
            value = seconds / dayInSeconds;
            unit = 'day';
        }
        else if (seconds >= hourInSeconds) {
            value = seconds / hourInSeconds;
            unit = 'hour';
        }
        else if (seconds >= minInSeconds) {
            value = seconds / minInSeconds;
            unit = 'minute';
        }
        return intl.formatRelativeTime(Math.floor(-value), unit);
    },
    /**
     * Returns a global resolved options object.
     */
    getResolvedOptions() {
        if (!global.resolvedOptions) {
            const resolvedOptions = new Intl.DateTimeFormat().resolvedOptions();
            global.resolvedOptions = Object.assign(Object.assign({}, resolvedOptions), { locale: resolvedOptions.locale.split('-')[0] });
        }
        return global.resolvedOptions;
    },
    getOrderedLanguages(languages) {
        let currentGeneralLanguage = null;
        const finalOrderedLanguages = [];
        for (const currentLanguage of languages) {
            if (currentGeneralLanguage != null &&
                currentLanguage.language !== currentGeneralLanguage.toLocaleString()) {
                finalOrderedLanguages.push(currentGeneralLanguage, currentLanguage);
                if (currentLanguage.region) {
                    currentGeneralLanguage = new Intl.Locale(currentLanguage.language);
                }
                else {
                    currentGeneralLanguage = null;
                }
            }
            else {
                finalOrderedLanguages.push(currentLanguage);
                if (currentLanguage.region) {
                    currentGeneralLanguage = new Intl.Locale(currentLanguage.language);
                }
            }
        }
        if (currentGeneralLanguage != null) {
            finalOrderedLanguages.push(currentGeneralLanguage);
        }
        return finalOrderedLanguages;
    },
    /**
     * Looks for the first allowed locale from the list of locales.
     * In case there is not a direct match with the country specifier, resolve the language anyway.
     *
     * @param allowedLocales allowed locales
     * @param locales possible locales
     * @returns the first locale to be present in allowed locales, undefined otherwise
     */
    getValidLocale(allowedLocales, locales) {
        const finalLocales = this.getOrderedLanguages(locales);
        let fallback = null;
        const validLocale = finalLocales.find(locale => {
            if (allowedLocales.includes(locale)) {
                return true;
            }
            else {
                const value = allowedLocales.find(l => l.language === locale.toLocaleString());
                if (!fallback && value) {
                    fallback = value;
                }
            }
        });
        return fallback !== null && fallback !== void 0 ? fallback : validLocale;
    },
    /**
     * Returns the text key to display from messageDescriptor.id and component id.
     *
     * @param messageDescriptor to get the id from
     * @param componentId the id of the component
     * @returns the text key to display.
     */
    getTextKeyToDisplay(messageDescriptor, componentId) {
        const { id } = messageDescriptor;
        if (!!id && !!componentId) {
            const key = id.slice(id.lastIndexOf('/') + 1);
            return `${componentId}.${key}`;
        }
        return '';
    },
    /**
     * Checks if the given language is in the list of translated languages.
     *
     * @param {string} language - The language code to check.
     * @param {string} supportedLanguages - The languages that have translations
     * @returns {boolean} - Returns true if the language is in the list of translated languages, otherwise false.
     */
    isSupportedLanguage(language, supportedLanguages) {
        const SUPPORTED_LANGUAGES = new Set(supportedLanguages);
        return SUPPORTED_LANGUAGES.has(language);
    }
};
exports.default = IntlHelper;
//# sourceMappingURL=IntlHelper.js.map